import React, { useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

import { Container, ButtonPrimary } from 'components';
import {
  Title,
  Form,
  Error,
  FormGroup,
  Input,
  HelperText,
  NavigationButtons,
  SkipPoNumberText,
} from 'components/checkout';
import { currentRmaState, checkoutState, accountState } from 'state/atoms';
import { CHECKOUT_BILLING_STEP } from 'utils/constants';
import { storageCheckoutSelector } from 'state/selectors';
import { defaultCheckoutState } from 'state/defaults';
import { getTotalCasePrice } from 'utils/helpers';
import { useCheckoutStepsValidation } from 'utils/hooks';

import { getValidationSchema } from './validationSchema';

const CheckoutBilling = () => {
  const history = useHistory();
  const { items } = useRecoilValue(currentRmaState);
  const accountData = useRecoilValue(accountState);
  const checkout = useRecoilValue(checkoutState);
  const setCheckout = useSetRecoilState(storageCheckoutSelector);
  const validatePreviousSteps = useCheckoutStepsValidation(CHECKOUT_BILLING_STEP);

  const caseHasCost =
    getTotalCasePrice(
      items,
      checkout.shipping.method,
      accountData.getDefaultBillToAddress,
      accountData.currencyConversionRate
    ) > 0;

  const { watch, errors, register, trigger, getValues } = useForm({
    mode: 'onChange',
    defaultValues: { poNumber: checkout.billing.poNumber },
    resolver: (values, context) => {
      // Middleware for passing context to yup schema
      const customResolver = yupResolver(getValidationSchema());
      return customResolver(values, {
        ...context,
        caseHasCost,
      });
    },
  });

  const poNumber = watch('poNumber');

  useEffect(() => {
    validatePreviousSteps().then((stepsAreValid) => {
      if (stepsAreValid && checkout.currentStep !== CHECKOUT_BILLING_STEP) {
        setCheckout({
          ...checkout,
          currentStep: CHECKOUT_BILLING_STEP,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Run validation if form was filled before
    if (checkout.billing.touched) {
      trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBackClick = () => {
    setCheckout({
      ...checkout,
      billing: defaultCheckoutState.billing,
    });
    history.push('/cases/new/checkout/shipping');
  };

  const handleNextClick = () => {
    trigger().then((formIsValid) => {
      if (formIsValid) {
        setCheckout({
          ...checkout,
          billing: { ...getValues(), touched: true },
        });
        history.push('/cases/new/checkout/review');
      }
    });
  };

  return (
    <Container>
      <Title>Purchase Order Info</Title>
      <Form>
        <Error>{errors.poNumber && errors.poNumber.message}</Error>
        <FormGroup>
          <label>PO Number</label>
          <div>
            <Input name="poNumber" error={errors.poNumber} ref={register} maxLength={50} />
          </div>
          <HelperText>{50 - poNumber.length} characters remaining</HelperText>
          {!caseHasCost ? (
            <SkipPoNumberText>(Select NEXT if not applicable)</SkipPoNumberText>
          ) : null}
        </FormGroup>
      </Form>
      <NavigationButtons>
        <ButtonPrimary onClick={handleBackClick}>BACK</ButtonPrimary>
        <ButtonPrimary onClick={handleNextClick}>NEXT</ButtonPrimary>
      </NavigationButtons>
    </Container>
  );
};

export { CheckoutBilling };
